import React, { useState } from 'react';
import { AwaitingItem, DestinationGroup, NetworkLocation } from '../types';

interface Props {
  groups: DestinationGroup[];
  /** Ključevi izabranih tokova (flowKey), redom obilaska. */
  selected: string[];
  onToggle: (flowKey: string) => void;
  maxWaitDays: number;
  /** Ključevi naloga izbačenih iz ture, oblika „tok|nalog". */
  excluded: string[];
  onToggleDocument: (flowKey: string, document: string | null) => void;
  /** Šifarnik, da tok piše „Bečej → Apatin" a ne šifre. */
  locations?: NetworkLocation[];
}

/** Ključ pod kojim se nalog izbacuje. Isti oblik i ovde i u App-u. */
export function documentKey(flowKey: string, document: string | null): string {
  return `${flowKey}|${document ?? ''}`;
}

/** „MPO 002" → „Apatin", „MAG 021" → naziv magacina. Šifra kad naziva nema. */
function placeLabel(reference: string | null, locations: NetworkLocation[]): string {
  if (!reference) return 'izvor nepoznat';

  const match = reference.match(/^(MPO|MAG) (\d{3})$/);
  if (!match) return reference;

  const kind = match[1] === 'MAG' ? 'Warehouse' : 'Store';
  const location = locations.find((l) => l.kind === kind && l.code === match[2]);
  if (!location) return reference;

  const name = location.city || location.name;
  return match[1] === 'MAG' ? name : name.replace(/^PRODAJNO MESTO\s+/i, '');
}

/** Nalozi jedne grupe, sa količinom i najdužim čekanjem. */
function byDocument(items: AwaitingItem[]) {
  const map = new Map<string, { document: string | null; items: AwaitingItem[] }>();

  for (const item of items) {
    const key = item.sourceDocument ?? '';
    const bucket = map.get(key);
    if (bucket) bucket.items.push(item);
    else map.set(key, { document: item.sourceDocument, items: [item] });
  }

  return Array.from(map.values())
    .map((bucket) => ({
      document: bucket.document,
      items: bucket.items,
      quantity: bucket.items.reduce((sum, i) => sum + i.quantity, 0),
      waitingDays: Math.max(...bucket.items.map((i) => i.waitingDays))
    }))
    .sort((a, b) => b.waitingDays - a.waitingDays);
}

/**
 * Roba koja čeka na transport, grupisana po TOKU: od tačke A do tačke B.
 *
 * Redosled gledanja prati operaterovu logiku: prvo šta iz magacina treba da
 * ide (glavni tovar), pa šta stoji po objektima i uzima se usput — kao što se
 * i kod kupaca gleda gde roba fizički stoji. Nalozi stariji od mesec dana su
 * sklonjeni po strani — ne traže ništa i ne ulaze u predloge, ali se mogu
 * staviti na turu kad zatrebaju.
 *
 * Tok se može i <b>razložiti na naloge</b>, jer tovar ne stane uvek: tada
 * operater skida ceo nalog, a ne komad. Nalog se kod nas ne deli — ako treba
 * manje od celog, razbija se u ERP-u.
 */
export default function AwaitingPanel({
  groups, selected, onToggle, maxWaitDays, excluded, onToggleDocument, locations = []
}: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (groups.length === 0) {
    return <p className="empty">Nema robe koja čeka na transport.</p>;
  }

  const live = groups.filter((g) => !g.isStale && !g.isExternal);

  // „Iz magacina" je SAMO centralni (021) — odatle tura kreće, pa se ta roba
  // tovari pri polasku. Lokalni magacini (026 Bečej, 029 Subotica) su objekti
  // kao i prodavnice: tura mora da svrati i uzme.
  const fromWarehouse = live.filter((g) => g.origin === 'MAG 021');
  const enRoute = live.filter((g) => g.origin !== 'MAG 021');
  const stale = groups.filter((g) => g.isStale);
  const external = groups.filter((g) => g.isExternal);

  const renderGroup = (group: DestinationGroup, showOrigin: boolean) => {
    // Eksterni razvoz nema dugme „Na turu": vozi ga treće lice, a ovde stoji
    // samo da se stanje magacina slaže sa ERP-om.
    const planable = !group.isExternal;
    const position = selected.indexOf(group.flowKey);
    const isSelected = position >= 0;
    const documents = byDocument(group.items);
    const isExpanded = expanded === group.flowKey;

    const droppedQuantity = documents
      .filter((d) => excluded.includes(documentKey(group.flowKey, d.document)))
      .reduce((sum, d) => sum + d.quantity, 0);

    const destinationLabel = placeLabel(group.destination, locations);
    const originLabel = placeLabel(group.origin, locations);

    return (
      <li key={group.flowKey}>
        {/* Klik na karticu RAZLAŽE tok, ne stavlja ga na turu. Na turu se ide
            izričitim dugmetom sa strane. */}
        <button
          type="button"
          className={`destination ${isSelected ? 'destination--selected' : ''} ${
            group.hasOverdue && !group.isStale ? 'destination--overdue' : ''
          }`}
          aria-expanded={isExpanded}
          onClick={() => setExpanded(isExpanded ? null : group.flowKey)}
        >
          <span className="destination__name">
            {isSelected && <span className="destination__order">{position + 1}</span>}
            {showOrigin ? (
              <>
                <span className={group.origin ? undefined : 'flow__unknown'}>{originLabel}</span>
                {' → '}
                {destinationLabel}
              </>
            ) : (
              destinationLabel
            )}
            <span className="flow__code"> {group.destination}</span>
            {group.zaKupce && <span className="flag"> za kupca</span>}
          </span>
          <span className="destination__meta">
            {group.items.length} stavki · {group.totalQuantity} kom
            {/* Izbačeno se prikazuje na samoj grupi: inače se ne vidi da tura
                nosi manje nego što tok čeka. */}
            {droppedQuantity > 0 && (
              <strong className="destination__dropped"> · {droppedQuantity} kom izbačeno</strong>
            )}
          </span>
          <span className="destination__waiting">
            {group.zaKupce ? (
              <>kupac čeka {group.oldestWaitingDays} d — roba ide pravo u objekat</>
            ) : group.isStale ? (
              <>verovatno neproknjižen prijem</>
            ) : group.hasOverdue ? (
              <strong>
                {group.overdueCount} od {group.items.length} stavki preko {maxWaitDays} d
              </strong>
            ) : (
              <>sve unutar {maxWaitDays} d</>
            )}
          </span>
          <span className="destination__age">najstarije čeka {group.oldestWaitingDays} d</span>
          {group.needsReviewCount > 0 && (
            <span className="destination__review">
              {group.needsReviewCount} bez potvrđenog odredišta
            </span>
          )}
        </button>

        <div className="destination__act">
          {planable ? (
            <button
              type="button"
              className={isSelected ? '' : 'primary'}
              onClick={() => onToggle(group.flowKey)}
              aria-pressed={isSelected}
            >
              {isSelected ? 'Skini sa ture' : 'Na turu'}
            </button>
          ) : (
            <span className="flag">ne vozimo mi</span>
          )}

          <span className="muted">
            {documents.length}{' '}
            {group.zaKupce
              ? (documents.length === 1 ? 'otpremnica' : 'otpremnice')
              : (documents.length === 1 ? 'nalog' : 'naloga')}
            {isExpanded ? ' · razloženo' : ''}
          </span>
        </div>

        {isExpanded && (
          <div className="orders">
            {documents.map((entry) => {
              const key = documentKey(group.flowKey, entry.document);
              const isDropped = excluded.includes(key);

              return (
                <div key={key} className={`orders__row ${isDropped ? 'orders__row--dropped' : ''}`}>
                  <span className="orders__name">
                    {entry.items[0]?.zaKupca
                      ? `Kupac ${entry.items[0].zaKupca.customer || '(bez imena)'} — otpremnica ${entry.document}`
                      : entry.document ? `Nalog ${entry.document}` : 'Bez naloga'}
                    <span className="muted">
                      {' '}· {entry.quantity} kom · čeka {entry.waitingDays} d
                    </span>
                  </span>

                  <span className="orders__items muted">
                    {entry.items.map((i) => `${i.articleName} × ${i.quantity}`).join(', ')}
                  </span>

                  {/* Nalog se prikazuje CEO, kao u ERP-u — ali kad na isti
                      artikal polaze pravo vise naloga nego sto ima robe, to
                      mora da pise. Ranije je stavka tiho ispadala sa naloga i
                      operater je video krnj nalog bez ijedne reci zasto. */}
                  {entry.items.some((i) => i.isShort) && (
                    <span className="orders__short">
                      Magacin ne pokriva sve:{' '}
                      {entry.items
                        .filter((i) => i.isShort)
                        .map((i) =>
                          `${i.articleName} — traži ${i.quantity}, ima ${i.availableForArticle ?? 0} za sve naloge`)
                        .join('; ')}
                      . Proveri sa magacinom pre utovara.
                    </span>
                  )}

                  <button type="button" onClick={() => onToggleDocument(group.flowKey, entry.document)}>
                    {isDropped ? 'Vrati na turu' : 'Izbaci sa ture'}
                  </button>
                </div>
              );
            })}

            {group.zaKupce ? (
              <p className="note">
                Roba za kupca: ide pravo u objekat, gde je kupac preuzima (ili je
                prodavnica vozi). Druga noga — 012 → objekat koji je prodao — upisuje
                se sama kad se tura zatvori.
              </p>
            ) : (
              <p className="note">
                Skida se ceo nalog. Ako treba manje od njega, razbij nalog u ERP-u
                na dva — inače objekat dobija nalog koji ne može da primi u
                celosti.
              </p>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="flows">
      {fromWarehouse.length > 0 && (
        <section className="flows__section">
          <h3 className="flows__title">Iz magacina</h3>
          <ul className="destinations">
            {fromWarehouse.map((g) => renderGroup(g, false))}
          </ul>
        </section>
      )}

      {enRoute.length > 0 && (
        <section className="flows__section">
          <h3 className="flows__title">Po objektima — uzima se usput</h3>
          <p className="note">
            Roba ovih tokova fizički stoji kod pošiljaoca. Tura koja tuda
            prolazi je uzima; ako odredište nije na turi, ide do magacina pa
            drugom turom dalje.
          </p>
          <ul className="destinations">
            {enRoute.map((g) => renderGroup(g, true))}
          </ul>
        </section>
      )}

      {external.length > 0 && (
        <details className="flows__stale">
          <summary>
            Ne vozimo mi — eksterni razvoz ({external.reduce((n, g) => n + g.items.length, 0)} stavki
            {' · '}{external.reduce((n, g) => n + g.totalQuantity, 0)} kom)
          </summary>
          <p className="note">
            Roba sa odredištem 090 — vozi je treće lice. Stoji ovde da bi se
            stanje magacina slagalo sa ERP-om; na turu ne ide.
          </p>
          <ul className="destinations">
            {external.map((g) => renderGroup(g, true))}
          </ul>
        </details>
      )}

      {stale.length > 0 && (
        <details className="flows__stale">
          <summary>
            Ostavljeno po strani ({stale.reduce((n, g) => n + g.items.length, 0)} stavki
            {' · '}najstarije {Math.max(...stale.map((g) => g.oldestWaitingDays))} dana)
          </summary>
          <p className="note">
            Nalozi stariji od mesec dana. Ne traže ništa od tebe i ne ulaze u
            predloge — stoje ovde da se ne izgube. Ako ti neki zatreba, otvori i
            stavi ga na turu kao i svaki drugi.
          </p>
          <ul className="destinations">
            {stale.map((g) => renderGroup(g, true))}
          </ul>
        </details>
      )}
    </div>
  );
}
